import { useEffect, useRef, useState } from 'react';
import { SlArrowDown } from 'react-icons/sl';
import { FaVolumeMute, FaVolumeUp, FaRedo } from 'react-icons/fa';

import spotCIG from '../../../assets/mouseFollow/Spot-Boso-1.webm';
import spotCIGMobile from '../../../assets/mouseFollow/verticale-per-sito.webm';

import firstFrame from '../../../assets/mouseFollow/firstFrameSpot.png';
import firstFrameMobile from '../../../assets/mouseFollow/frame-screen-vertical.png';

export default function HeroSpot({ scrollTargetRef }) {
    const sectionRef = useRef(null);
    const videoDesktopRef = useRef(null);
    const videoMobileRef = useRef(null);
    const frameDesktopRef = useRef(null);
    const frameMobileRef = useRef(null);
    const overlayRef = useRef(null);
    const isMobileRef = useRef(false);
    const phaseRef = useRef('frame');

    const [isMuted, setIsMuted] = useState(true);
    const [phase, setPhase] = useState('frame'); // 'frame' | 'video'
    const [hasPlayedOnce, setHasPlayedOnce] = useState(false);

    useEffect(() => {
        phaseRef.current = phase;
    }, [phase]);

    const getActiveVideo = () =>
        isMobileRef.current ? videoMobileRef.current : videoDesktopRef.current;

    const getActiveFrame = () =>
        isMobileRef.current ? frameMobileRef.current : frameDesktopRef.current;

    const hideContent = () => {
        if (overlayRef.current) overlayRef.current.style.opacity = '0';
    };

    const showContent = () => {
        if (overlayRef.current) overlayRef.current.style.opacity = '1';
    };

    const dispatchHeroVideoState = (playing) => {
        window.dispatchEvent(new CustomEvent('hero-video-state', { detail: { playing } }));
    };

    const playVideo = () => {
        const video = getActiveVideo();
        const frame = getActiveFrame();
        if (!video) return;

        video.muted = true;
        setIsMuted(true);
        video
            .play()
            .then(() => {
                if (frame) frame.style.opacity = '0';
                video.style.opacity = '1';
                setPhase('video');
                setHasPlayedOnce(true);
                dispatchHeroVideoState(true);
            })
            .catch(() => {});
    };

    useEffect(() => {
        isMobileRef.current = window.innerWidth < 768;

        const video = getActiveVideo();
        if (!video) return;

        video.currentTime = 0;

        const startTimer = setTimeout(() => {
            hideContent();
            setTimeout(playVideo, 700);
        }, 3000);

        const handleEnded = () => {
            const frame = getActiveFrame();
            video.pause();
            video.style.opacity = '0';
            if (frame) frame.style.opacity = '1';
            setPhase('frame');
            showContent();
            dispatchHeroVideoState(false);
        };

        video.addEventListener('ended', handleEnded);

        return () => {
            clearTimeout(startTimer);
            video.removeEventListener('ended', handleEnded);
            dispatchHeroVideoState(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pause the video while the hero scrolls out of view, resume when it scrolls back in.
    useEffect(() => {
        const node = sectionRef.current;
        if (!node || typeof IntersectionObserver === 'undefined') return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const video = getActiveVideo();
                if (!video || phaseRef.current !== 'video') return;

                if (entry.isIntersecting) {
                    if (video.paused) {
                        video.play().then(() => dispatchHeroVideoState(true)).catch(() => {});
                    }
                } else if (!video.paused) {
                    video.pause();
                    dispatchHeroVideoState(false);
                }
            },
            { threshold: 0.25 }
        );

        observer.observe(node);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleMute = () => {
        const video = getActiveVideo();
        if (!video) return;
        video.muted = !video.muted;
        setIsMuted(video.muted);
    };

    const replay = () => {
        const video = getActiveVideo();
        if (!video) return;
        video.currentTime = 0;
        hideContent();
        setTimeout(playVideo, 700);
    };

    const scrollToNext = () => {
        if (scrollTargetRef?.current) {
            scrollTargetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
        }
    };

    return (
        <section ref={sectionRef} className="relative z-20 h-lvh overflow-hidden bg-black">
            {/* FRAME INIZIALE DESKTOP */}
            <img
                ref={frameDesktopRef}
                src={firstFrame}
                alt=""
                aria-hidden="true"
                loading="eager"
                className="hidden md:block absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-700"
            />

            {/* FRAME INIZIALE MOBILE */}
            <img
                ref={frameMobileRef}
                src={firstFrameMobile}
                alt=""
                aria-hidden="true"
                loading="eager"
                className="md:hidden absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-700"
            />

            {/* VIDEO DESKTOP */}
            <video
                ref={videoDesktopRef}
                preload="auto"
                playsInline
                aria-hidden="true"
                className="hidden md:block absolute top-0 left-0 w-full h-full object-cover opacity-0 transition-opacity duration-700"
            >
                <source src={spotCIG} type="video/webm" />
            </video>

            {/* VIDEO MOBILE */}
            <video
                ref={videoMobileRef}
                preload="auto"
                playsInline
                aria-hidden="true"
                className="md:hidden absolute top-0 left-0 w-full h-full object-cover opacity-0 transition-opacity duration-700"
            >
                <source src={spotCIGMobile} type="video/webm" />
            </video>

            {/* OVERLAY + CONTENT */}
            <div
                ref={overlayRef}
                className="absolute top-0 left-0 bg-opacity-0 w-full h-full flex justify-center mini-screen:backdrop-blur-sm transition-opacity duration-700"
            >
            <div className="absolute top-0 left-0 w-full h-full bg-black/50"></div>

                <div className="absolute bottom-0 left-0 text-left pb-56 px-8 md:p-32 mini-screen:pb-6 mini-screen:text-black text-white">
                    <h1 className="text-4xl md:text-7xl font-semibold drop-shadow-2xl mini-screen:text-xl">
                        <span>L’oro accessibile a tutti,<br />prima di tutti</span>
                        <span className="sr-only z-40 text-xs absolute top-[2%] text-white left-3/4 transform -translate-x-1/2">
                            Careisgold: la prima azienda a rendere l’Oro da investimento accessibile a ogni famiglia.
                        </span>
                    </h1>
                    <div className="text-justify">
                        <p className="text-md md:text-xl mt-4 drop-shadow-2xl">
                            <span className="text-md md:text-xl drop-shadow-2xl">Careisgold:{' '}</span>la prima azienda a rendere l’Oro da investimento<br />accessibile a ogni famiglia.
                        </p>
                    </div>
                </div>

                <SlArrowDown
                    onClick={scrollToNext}
                    role="button"
                    aria-label="Scorri alla sezione successiva"
                    tabIndex={0}
                    className="arrow cursor-pointer block absolute transform translate-x-1/2 text-slate-50 text-3xl md:text-5xl mini-screen:hidden"
                />
            </div>

            {/* PULSANTE AUDIO */}
            {phase === 'video' && (
                <button
                    type="button"
                    onClick={toggleMute}
                    aria-label={isMuted ? 'Attiva audio' : 'Disattiva audio'}
                    className="absolute bottom-20 right-8 z-30 bg-black/60 hover:bg-black/80 text-white rounded-full p-3 transition-all duration-300"
                >
                    {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                </button>
            )}

            {/* PULSANTE REPLAY */}
            {phase === 'frame' && hasPlayedOnce && (
                <button
                    type="button"
                    onClick={replay}
                    aria-label="Rivedi il video"
                    className="absolute bottom-20 right-8 z-30 bg-black/60 hover:bg-black/80 text-white rounded-full p-3 transition-all duration-300"
                >
                    <FaRedo />
                </button>
            )}
        </section>
    );
}
